/*
 * ============================================================================
 * DATOS FICTICIOS — escenario de pruebas. Los agentes, departamentos,
 * usuarios y tenants de este simulador son inventados y NO describen el
 * producto ni lo desplegado. Solo se usa en desarrollo y en las pruebas del
 * Pórtico; en producción está apagado.
 * ============================================================================
 */

/**
 * Cliente API de Pórtico — REST + SSE
 * Ref: 02-contrato-descriptor-sesion.md §2 (endpoints v1)
 *
 * ÚNICO punto de cambio mock -> real (R-PT-02). Cuando VITE_USE_MOCK=true
 * todas las llamadas resuelven contra el mock local. En modo real habla con
 * el kernel (VITE_API_BASE; en dev, el proxy /v1 de vite.config.js) con
 * Authorization: Bearer <ID token de Cognito> en TODAS las llamadas
 * (R-PT-04/05) — el token lo aporta src/auth/cognito.js, nunca el caller.
 */

import { MOCK_DESCRIPTOR } from '../mock/descriptor.js'
import { getMockTurn }     from '../mock/sse-events.js'
import { getBearerToken }  from '../auth/cognito.js'
import { getRuntimeConfig } from '../config/runtimeConfig.js'
import { ApiError }        from './errors.js'
import {
  listMockUsers,
  inviteMockUser,
  changeMockUserRole,
} from '../mock/admin-users.js'
import { empleadosMock, empleadosPedidos } from '../mock/empleados.js'
import {
  proposeMockAction,
  approveMockAction,
  filterPurgedChats,
  rawGrant,
} from '../mock/actions-store.js'
import {
  listFilesMock,
  uploadInboxMock,
  readFileMock,
  getFilePolicyMock,
} from '../mock/zft-files.js'
import {
  listMecanismosMock,
  getEsquemaMock,
  listObjetosMock,
  getObjetoMock,
  getVersionesMock,
  getRegistrosMock,
  proponerAccionMock,
  getAccionMock,
} from '../mock/mecanismos.js'

const USE_MOCK = import.meta.env.VITE_USE_MOCK !== 'false'
export const isMock = USE_MOCK

// Identidad que el mock delegará en el bootstrap (tenant + usuario): permite
// al Login previsualizar marca e identidad ANTES del bootstrap SOLO en modo
// mock — data-driven desde el descriptor, sin ramas por tenant (US-58). En
// modo real es null: antes de entrar no hay tenant conocido.
export const mockLoginPreview = USE_MOCK
  ? { tenant: MOCK_DESCRIPTOR.tenant, user: MOCK_DESCRIPTOR.user }
  : null
// Producción: la URL de la API viene de config.json (la escribe el despliegue).
// Dev sin config.json ni .env.local: '/v1' -> proxy de vite hacia el kernel local.
// GEN-276: sale de config.json (cargado en main.jsx), con el entorno como respaldo en dev/tests.
const apiBase = () => getRuntimeConfig().apiBase

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

// Sesión caducada (401 del kernel): el cliente no decide nada (R-PT-01),
// solo avisa a SessionContext para forzar el re-login.
let onSessionExpired = null
export function setSessionExpiredHandler(fn) {
  onSessionExpired = fn
}

function authHeaders(json = true) {
  const h = {}
  if (json) h['Content-Type'] = 'application/json'
  const token = getBearerToken()
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

// Único tratamiento del 401 (contrato §4: access 60 min, idle 30 min,
// absoluta 8 h): tirar la sesión local y obligar a volver a entrar.
function checkAuth(res, op) {
  if (res.status === 401) {
    onSessionExpired?.()
    throw new Error(`${op}: la sesión ha caducado (401)`)
  }
  return res
}

// Construye un ApiError con el `code` estable del envelope de error OBJETIVO del
// kernel ({ error: { code, message } }). Hoy el kernel real aún devuelve string
// plano, así que si el cuerpo no trae `code` se deriva uno del status
// (403 -> forbidden) para que la UI pueda mapearlo igualmente. Al migrar el
// kernel al envelope estructurado, este helper ya lo consume sin cambios.
async function apiError(res, op) {
  let code = null
  let message = `${op} falló (${res.status})`
  try {
    const body = await res.json()
    if (body?.error) {
      code = body.error.code ?? null
      message = body.error.message ?? message
    }
  } catch { /* respuesta sin cuerpo JSON */ }
  if (!code) {
    if (res.status === 403) code = 'forbidden'
    else if (res.status === 404 && op === 'approveAction') code = 'accion_no_encontrada'
    else if (res.status === 404 && op === 'proposeChatAction') code = 'chat_no_encontrado'
    else code = 'unknown_error'
  }
  return new ApiError(code, message, res.status)
}

/**
 * Simula un stream SSE emitiendo los eventos del mock uno a uno con un
 * pequeño retardo entre ellos, llamando a onEvent para cada uno.
 * Llama a onDone cuando el stream termina.
 */
async function streamMockEvents(events, { onEvent, onDone, onError }) {
  try {
    for (const event of events) {
      await new Promise(r => setTimeout(r, event.type === 'progress' ? 600 : 300))
      onEvent(event)
      if (event.type === 'done') break
    }
    onDone()
  } catch (err) {
    onError(err)
  }
}

// Solo mock: `?perfil=usuario` o `?perfil=champion` entran como un perfil no admin (sin las entradas de
// administración en el menú, que el kernel real solo emite al admin). Personas, Agentes y Saber siguen: el
// kernel los emite a todos (GEN-PX-022). Sirve para ver cada perfil sin «Invitar» ni la gestión de agentes.
const MENU_SOLO_ADMIN = new Set(['admin', 'ficheros', 'equipo', 'agentes-permisos', 'herramientas', 'detenciones', 'coste', 'modelos', 'salud', 'libro-de-estilo'])
function conHijosDelPerfil(m, ver = h => !MENU_SOLO_ADMIN.has(h.id)) {
  if (!m.hijos) return m
  const hijos = m.hijos.filter(ver)
  const { hijos: _quitados, ...resto } = m
  return hijos.length ? { ...resto, hijos } : resto
}
export function perfilMockPedido() {
  const m = typeof window !== 'undefined' ? /[?&]perfil=(usuario|champion)\b/.exec(window.location?.search ?? '') : null
  return m ? m[1] : null
}
function descriptorMockPorPerfil(d) {
  const rol = perfilMockPedido()
  if (!rol) return d
  // Nombres de los empleados del mock (mock/empleados.js), para que el perfil propio coincida con el directorio.
  const quien = rol === 'champion' ? { id: 'user-ana-002', display_name: 'Ana Torres' } : { id: 'user-luis-003', display_name: 'Luis Sáez' }
  return {
    ...d,
    user: { ...d.user, ...quien, rol },
    // Los guardarraíles (`mecanismo:*`) los emite el kernel solo a quien tiene su scope de entrada (admin).
    // Auditoría (GEN-PX-016): el kernel da al champion solo la pestaña Saber y al usuario nada.
    // Carril (GEN-PX-023): los hijos de cada botón son los que el kernel da al perfil; los de gestión no salen (y sin hijos, el botón no abre carril).
    capabilities: { ...d.capabilities, menu: d.capabilities.menu.filter(m => !MENU_SOLO_ADMIN.has(m.id) && !m.id.startsWith('mecanismo:')).flatMap(m => m.id !== 'auditoria' ? [conHijosDelPerfil(m)] : rol === 'champion' ? [conHijosDelPerfil(m, h => h.id === 'saber')] : []) },
  }
}

// ---------------------------------------------------------------------------
// POST /v1/session/bootstrap → descriptor (proyección segura)
// R-PT-01: sin lógica de negocio. R-PT-03: solo proyección segura.
// Real (contrato §3): Bearer en cabecera y SIN credenciales en el body —
// la identidad viene de Cognito, el kernel resuelve rol y capacidades.
// ---------------------------------------------------------------------------
export async function bootstrap() {
  if (USE_MOCK) {
    // Simular latencia de red
    await new Promise(r => setTimeout(r, 400))
    // US-467: la lista de chats del descriptor excluye los purgados, igual que
    // el kernel deja de listar un chat borrado tras `purgar_chat`. Así, tras
    // borrar, el chat NO reaparece al rehidratar (invariante US-62/US-65).
    return {
      token: 'mock-bearer-token-001',
      descriptor: descriptorMockPorPerfil({ ...MOCK_DESCRIPTOR, chats: filterPurgedChats(MOCK_DESCRIPTOR.chats) }),
    }
  }

  const res = checkAuth(
    await fetch(`${apiBase()}/session/bootstrap`, {
      method: 'POST',
      headers: authHeaders(),
    }),
    'bootstrap',
  )
  if (!res.ok) throw new Error(`Bootstrap falló: ${res.status}`)
  const descriptor = await res.json()
  return { token: getBearerToken(), descriptor }
}

// ---------------------------------------------------------------------------
// GET /v1/chats/{chat_id} → historial paginado (R-PT-06, B2b)
// El kernel devuelve páginas de `turnos` ({seq, mensaje_usuario, respuesta,
// created_at}) con `next_cursor`; aquí se aplanan a la lista de mensajes que
// pinta ChatWindow (dos burbujas por turno). 404 = chat aún sin turnos
// persistidos (o ajeno) -> historial vacío.
// ---------------------------------------------------------------------------
export async function getChatHistory(chatId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    // Devuelve conversación de muestra según el chat_id
    return {
      chat_id: chatId,
      messages: MOCK_CHAT_HISTORY[chatId] ?? [],
    }
  }

  const turnos = []
  let cursor = null
  do {
    const q = new URLSearchParams({ limit: '100' })
    if (cursor) q.set('cursor', cursor)
    const res = checkAuth(
      await fetch(`${apiBase()}/chats/${encodeURIComponent(chatId)}?${q}`, {
        headers: authHeaders(false),
      }),
      'getChatHistory',
    )
    if (res.status === 404) return { chat_id: chatId, messages: [] }
    if (!res.ok) throw new Error(`getChatHistory falló: ${res.status}`)
    const page = await res.json()
    turnos.push(...page.turnos)
    cursor = page.next_cursor
  } while (cursor && turnos.length < 2000)

  const messages = turnos.flatMap(t => {
    // GEN-205: firma quien respondió; turno antiguo -> agente_destino -> anne.
    const agent = { id: t.agente ?? t.agente_destino ?? 'anne' }
    const user = {
      id:        `t-${t.seq}-user`,
      role:      'user',
      content:   t.mensaje_usuario,
      timestamp: t.created_at,
    }
    // GEN-PT-004: el turno trae lo que se pintó, EN ORDEN (respuesta, tarjetas de
    // acción con su estado actual, avisos). Un turno anterior a esto no lo trae:
    // se pinta como siempre, solo con la respuesta.
    const eventos = t.eventos?.length
      ? t.eventos
      : [{ type: 'answer', payload: { markdown: t.respuesta } }]
    return [
      // GEN-HU-016: el turno que reanuda uno en pausa no lleva mensaje de usuario propio.
      ...(t.mensaje_usuario ? [user] : []),
      ...eventos.map((e, i) => ({
        id:        `t-${t.seq}-agent-${i}`,
        role:      'agent',
        // GEN-HU-029: el coste del turno cuelga de su último mensaje (la respuesta).
        ...(t.coste_eur != null && i === eventos.length - 1
          ? { costeEur: t.coste_eur, ...(t.message_id ? { messageId: t.message_id } : {}) } : {}),
        // US-02: el historial refleja a quién se dirigió el turno. El kernel
        // devuelve el id del agente destino (default 'anne' en turnos previos).
        agent,
        type:      e.type,
        payload:   e.payload,
        timestamp: t.created_at,
      })),
    ]
  })
  // El agente_destino del último turno es el interlocutor actual del hilo (US-02):
  // permite reflejar en cabecera con quién se está hablando al reabrir.
  const threadAgent = turnos.length ? turnos[turnos.length - 1].agente_destino : null
  // GEN-HU-033: una @ es una llamada puntual; quien lleva el hilo (y el motivo)
  // es el último turno que NO fue una llamada.
  const last = [...turnos].reverse().find(t => t.motivo !== 'llamado') ?? null
  return {
    chat_id: chatId,
    messages,
    agente_destino: threadAgent ?? null,
    // GEN-205: interlocutor actual = quien respondió el último turno.
    agente: last ? (last.agente ?? last.agente_destino ?? 'anne') : null,
    // GEN-195: por qué se derivó el último turno (elegido|nombrado|tema|sigue|ninguno);
    // turno antiguo sin campo -> null (sin etiqueta).
    motivo: last?.motivo ?? null,
  }
}

// ---------------------------------------------------------------------------
// POST /v1/chats/{chat_id}/messages → stream SSE del turno (R-PT-07)
// Body { texto, agente_destino? } — el kernel rechaza campos extra. US-02:
// `agente_destino` dirige el turno a un agente concreto; si se omite, el
// kernel enruta por defecto a Anne-en-cliente (US-03).
// Parser SSE sobre fetch: EventSource no soporta POST ni Authorization.
// ---------------------------------------------------------------------------
// GEN-BUG-002: eventos que cuentan como respuesta del turno.
const RESPUESTAS_DE_TURNO = ['answer', 'escalated', 'action_preview']

export function sendMessage(chatId, content, { onEvent, onDone, onError, agenteDestino, reanuda } = {}) {
  if (USE_MOCK) {
    const events = getMockTurn(content)
    streamMockEvents(events, { onEvent, onDone, onError })
    // Devuelve un cancelador no-op (en real sería controller.abort())
    return { cancel: () => {} }
  }

  // Solo se envía la clave cuando hay destino explícito: así el default de
  // Anne lo decide el kernel (no el front) y el body sigue siendo { texto }
  // en el path por defecto (US-03).
  // GEN-HU-016: `reanuda` {action_id, decision} retoma el turno que quedó
  // esperando la firma (sin `texto`: lo pone el kernel).
  const body = {
    ...(reanuda ? { reanuda } : { texto: content }),
    ...(agenteDestino ? { agente_destino: agenteDestino } : {}),
  }

  const controller = new AbortController()
  fetch(`${apiBase()}/chats/${encodeURIComponent(chatId)}/messages`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
    signal: controller.signal,
  }).then(async res => {
    checkAuth(res, 'sendMessage')
    if (!res.ok) throw new Error(`sendMessage falló: ${res.status}`)
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    // GEN-BUG-002: un turno sin answer, escalated ni action_preview no es un fin normal.
    let hubo_respuesta = false
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop()
      for (const chunk of lines) {
        const dataLine = chunk.split('\n').find(l => l.startsWith('data:'))
        if (dataLine) {
          try {
            const event = JSON.parse(dataLine.slice(5))
            if (RESPUESTAS_DE_TURNO.includes(event?.type)) hubo_respuesta = true
            onEvent(event)
          } catch { /* ignorar líneas mal formadas */ }
        }
      }
    }
    if (!hubo_respuesta) throw new Error('sendMessage: el stream terminó sin respuesta')
    onDone()
  }).catch(err => {
    if (err.name !== 'AbortError') onError(err)
  })

  return { cancel: () => controller.abort() }
}

// ---------------------------------------------------------------------------
// GET /v1/actions/{action_id} — GEN-76·346 (D-1 de Werner): el enlace
// compartible. Misma proyección segura que `approveAction`, de solo lectura;
// el kernel autoriza con la MISMA función propio-o-tercero-con-rol que el
// approve — 404 igual si la acción no existe o no es visible para quien pide.
// ---------------------------------------------------------------------------
export async function getAction(actionId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 150))
    return getAccionMock(actionId)
  }

  const res = checkAuth(
    await fetch(`${apiBase()}/actions/${encodeURIComponent(actionId)}`, {
      headers: authHeaders(false),
    }),
    'getAction',
  )
  if (!res.ok) throw await apiError(res, 'getAction')
  return res.json()
}

// ---------------------------------------------------------------------------
// POST /v1/actions/{action_id}/approve — R-PT-11
// ---------------------------------------------------------------------------
export async function approveAction(actionId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 300))
    // El mock ejecuta la acción registrada (p.ej. purgar el chat); una acción
    // desconocida cae a éxito genérico (compat con las propuestas por SSE).
    return approveMockAction(actionId)
  }

  const res = checkAuth(
    await fetch(`${apiBase()}/actions/${encodeURIComponent(actionId)}/approve`, {
      method: 'POST',
      headers: authHeaders(false),
    }),
    'approveAction',
  )
  // SPEC #280 §B.4: propagar el `code` del envelope de error del kernel
  // ({ error: { code, message } }) para que la UI mapee 403/404/409 a un
  // mensaje estable, en vez de un Error genérico.
  if (!res.ok) throw await apiError(res, 'approveAction')
  return res.json()
}

// ---------------------------------------------------------------------------
// POST /v1/chats/{chat_id}/actions — US-467 (Opción B, #480). Canal REST para
// que el USUARIO INICIE una acción gobernada desde el rail, fuera de un turno.
// Corre A1+A2 (dry-run) y devuelve el `action_preview` con `action_id`; el
// efecto NO ocurre aquí — se ejecuta al aprobar por `approveAction` (misma
// puerta A2, IAM revalidado en A3/A4). Body { action_type, params } — el kernel
// rechaza campos extra. Errores (envelope): 403 forbidden · 404
// chat_no_encontrado · 422 accion_no_permitida | parametros_invalidos.
// ---------------------------------------------------------------------------
export async function proposeChatAction(chatId, actionType, params = {}) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 250))
    // Los `params` viajan TAMBIÉN al mock: es lo que valida A1 en el kernel, y
    // si el mock no los ve no puede reproducir su rechazo (ver #493/#494).
    return proposeMockAction(chatId, actionType, params)
  }

  const res = checkAuth(
    await fetch(`${apiBase()}/chats/${encodeURIComponent(chatId)}/actions`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ action_type: actionType, params }),
    }),
    'proposeChatAction',
  )
  if (!res.ok) throw await apiError(res, 'proposeChatAction')
  return res.json()
}

// ---------------------------------------------------------------------------
// Eliminar una conversación — US-467. Flujo gobernado de dos pasos, tal cual el
// contrato: PROPONE `purgar_chat` (dry-run) y CONFIRMA con el `approve`
// existente, que es donde se materializa la aprobación explícita del
// irreversible (borrado real en el chat store R0). El diálogo de confirmación
// del front gatea esta llamada; el borrado NUNCA es solo-local (R-PT-09): la
// verdad la fija el store y el rail se refresca desde el descriptor.
//
// `motivo` es OBLIGATORIO en A1 (`ParamsPurgarChat`: min_length=1) porque queda
// en la auditoría de la acción irreversible. En el canal del LLM lo redacta el
// modelo; en ESTE canal el usuario no escribe una razón (el diálogo es de dos
// pasos, sin campo libre), así que el front aporta un motivo determinista que
// dice DE DÓNDE vino la orden. Omitirlo hacía que el propose respondiera 422
// `parametros_invalidos` en prod — causa real del gate fallido de #494.
// ---------------------------------------------------------------------------
const MOTIVO_PURGA_USUARIO = 'Borrado solicitado por el usuario desde el historial de chats.'

export async function deleteChat(chatId) {
  const preview = await proposeChatAction(chatId, 'purgar_chat', {
    motivo: MOTIVO_PURGA_USUARIO,
  })
  return approveAction(preview.action_id)
}

// ===========================================================================
// GET /v1/detenciones — GEN-HU-005: qué norma detuvo qué (solo owner; la puerta
// vive en el kernel). -> { detenciones: [{fecha, agente, chat_id, message_id, norma, motivo}] }
export async function listDetenciones() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return { detenciones: [] }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/detenciones`, { headers: authHeaders(false) }),
    'listDetenciones',
  )
  if (!res.ok) throw await apiError(res, 'listDetenciones')
  return res.json()
}

// GET /v1/admin/coste?desde=YYYY-MM&hasta=YYYY-MM — GEN-MO-016: coste en euros por mes,
// modelo y paso (solo admin; la puerta vive en el kernel). Sin parámetros: últimos 12 meses.
// -> { meses: [{mes, sin_desglose_paso, filas: [{modelo, paso, eur, tokens, estimado}], total_eur}],
//      eur_por_usd, zona: 'UTC' }   (importes como cadena decimal; el mes actual primero)
export async function getCoste({ desde, hasta } = {}) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return {
      meses: [
        { mes: '2026-10', sin_desglose_paso: false, total_eur: '0.1472', filas: [
          { modelo: 'claude-opus-4-8', paso: 'turno', eur: '0.0920', tokens: 12000, estimado: false },
          { modelo: 'claude-sonnet-4-6', paso: 'juez_rt02', eur: '0.0552', tokens: 9000, estimado: false },
        ] },
        { mes: '2026-09', sin_desglose_paso: true, total_eur: '0.2000', filas: [
          { modelo: 'modelo-nuevo', paso: null, eur: '0.2000', tokens: 30000, estimado: true },
        ] },
      ],
      eur_por_usd: '0.92',
      zona: 'UTC',
    }
  }
  const qs = new URLSearchParams()
  if (desde) qs.set('desde', desde)
  if (hasta) qs.set('hasta', hasta)
  const q = qs.toString()
  const res = checkAuth(
    await fetch(`${apiBase()}/admin/coste${q ? `?${q}` : ''}`, { headers: authHeaders(false) }),
    'getCoste',
  )
  if (!res.ok) throw await apiError(res, 'getCoste')
  return res.json()
}

// GET/PUT /v1/admin/modelos — GEN-MO-009: catálogo de modelos de la credencial del tenant y la política
// (clase de llamada -> modelo). Solo admin (la puerta vive en el kernel). Sin claves.
// -> { catalogo: [{provider, model, default, seleccionable, tarifa: {entrada_usd_millon, salida_usd_millon, estimada}}],
//      default_credencial, politica: {declarada, juez, resumen, juez_confirmacion, agente_defecto,
//      actualizado_por, actualizado_en}, clases: [{clave, etiqueta, configurado, efectivo, origen, origen_texto}] }
// PUT con cambios parciales {clave: modelo|null} (null = «por defecto de la credencial») -> el mismo cuerpo.
// 422 `modelo_no_admitido` si el modelo no está en el catálogo (el fichero queda como estaba).
const MOCK_MODELOS = {
  catalogo: [
    { provider: 'anthropic', model: 'claude-sonnet-4-6', default: true, seleccionable: true, tarifa: { entrada_usd_millon: '3', salida_usd_millon: '15', estimada: false } },
    { provider: 'anthropic', model: 'claude-haiku-4-5', default: false, seleccionable: true, tarifa: { entrada_usd_millon: '1', salida_usd_millon: '5', estimada: false } },
    { provider: 'anthropic', model: 'claude-opus-4-8', default: false, seleccionable: true, tarifa: { entrada_usd_millon: '5', salida_usd_millon: '25', estimada: false } },
  ],
  default_credencial: 'claude-sonnet-4-6',
  politica: { declarada: true, juez: null, resumen: 'claude-haiku-4-5', juez_confirmacion: null, agente_defecto: null, actualizado_por: null, actualizado_en: null },
}
const MOCK_ETIQUETAS_MODELOS = {
  juez: 'Juez (normas semánticas)', resumen: 'Resumen del hilo',
  juez_confirmacion: 'Confirmación del juez', agente_defecto: 'Agente sin modelo en su ficha',
}
const MOCK_ORIGEN_NULL = {
  juez: ['default_credencial', 'Por defecto de la credencial'],
  resumen: ['modelo_del_turno', 'El modelo del turno (sin política para el resumen)'],
  juez_confirmacion: ['sin_escalado', 'Sin confirmación: el juez decide solo'],
  agente_defecto: ['default_credencial', 'Por defecto de la credencial'],
}
function vistaModelosMock() {
  const p = MOCK_MODELOS.politica
  return {
    ...MOCK_MODELOS,
    clases: Object.keys(MOCK_ETIQUETAS_MODELOS).map(clave => {
      const v = p[clave]
      const [origen, texto] = v ? ['politica', 'Política del tenant'] : MOCK_ORIGEN_NULL[clave]
      return {
        clave, etiqueta: MOCK_ETIQUETAS_MODELOS[clave], configurado: v,
        efectivo: v ?? (origen === 'default_credencial' ? MOCK_MODELOS.default_credencial : null),
        origen, origen_texto: texto,
      }
    }),
  }
}

export async function getModelos() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return vistaModelosMock()
  }
  const res = checkAuth(await fetch(`${apiBase()}/admin/modelos`, { headers: authHeaders(false) }), 'getModelos')
  if (!res.ok) throw await apiError(res, 'getModelos')
  return res.json()
}

export async function guardarModelos(cambios) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    for (const [k, v] of Object.entries(cambios)) {
      if (v != null && !MOCK_MODELOS.catalogo.some(m => m.model === v)) {
        throw new ApiError('modelo_no_admitido', `'${v}' no está en el catálogo de la credencial`, 422)
      }
      MOCK_MODELOS.politica[k] = v
    }
    MOCK_MODELOS.politica.actualizado_por = 'Nombre'
    MOCK_MODELOS.politica.actualizado_en = new Date().toISOString()
    return vistaModelosMock()
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/admin/modelos`, { method: 'PUT', headers: authHeaders(true), body: JSON.stringify(cambios) }),
    'guardarModelos',
  )
  if (!res.ok) throw await apiError(res, 'guardarModelos')
  return res.json()
}

// GET /v1/admin/salud — GEN-MO-018: salud de la instalación (solo admin; la puerta vive en el kernel).
// -> { estado: 'ok'|'degradado', comprobado_en, componentes: [{ id, nombre, estado, motivo, detalle }] }
//    estado de componente: 'ok' | 'degradado' | 'no_disponible'; la licencia llega 'no_gestionada' (GEN-208).
//    detalle del índice: { ultimo_proyectado, ultima_reconstruccion, pendientes, pendiente_desde,
//    retraso_max_s, plazo_s, vigentes, proyectados, al_dia }.
// En el mock, `?salud=degradado` en la URL simula un iKu pendiente desde hace 2 h.
function mockSalud() {
  const degradado = typeof window !== 'undefined' && /[?&]salud=degradado\b/.test(window.location?.search ?? '')
  const ahora = Date.now()
  const hace = s => new Date(ahora - s * 1000).toISOString()
  const indice = degradado
    ? { estado: 'degradado', motivo: '1 iKu(s) pendiente(s) de indexar; el más antiguo hace 2 h (plazo 1 h)',
        detalle: { ultimo_proyectado: hace(3 * 3600), ultima_reconstruccion: hace(86400), pendientes: 1,
                   pendiente_desde: hace(7200), retraso_max_s: 7200, plazo_s: 3600, vigentes: 3, proyectados: 2, al_dia: false } }
    : { estado: 'ok', motivo: 'al día',
        detalle: { ultimo_proyectado: hace(1800), ultima_reconstruccion: hace(86400), pendientes: 0,
                   pendiente_desde: null, retraso_max_s: null, plazo_s: 3600, vigentes: 3, proyectados: 3, al_dia: true } }
  return {
    estado: degradado ? 'degradado' : 'ok',
    comprobado_en: new Date(ahora).toISOString(),
    componentes: [
      { id: 'indice', nombre: 'Índice semántico', ...indice },
      { id: 'ekb', nombre: 'Base de conocimiento', estado: 'ok', motivo: 'la base responde', detalle: {} },
      { id: 'embeddings', nombre: 'Servicio de embeddings', estado: 'ok', motivo: 'la última llamada fue bien', detalle: {} },
      { id: 'licencia', nombre: 'Licencia', estado: 'no_gestionada', motivo: 'sin licencia gestionada (GEN-208)', detalle: {} },
    ],
  }
}

export async function getSalud() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return mockSalud()
  }
  const res = checkAuth(await fetch(`${apiBase()}/admin/salud`, { headers: authHeaders(false) }), 'getSalud')
  if (!res.ok) throw await apiError(res, 'getSalud')
  return res.json()
}

// GET /v1/admin/turnos/{message_id}/llamadas — GEN-MO-017: las llamadas a modelo de un turno (solo admin;
// la puerta vive en el kernel). `fecha` = created_at del turno: estrecha la ventana de búsqueda.
// -> { message_id, llamadas: [{paso, regla, clasificacion_origen, clase, modelo, input_tokens, output_tokens,
//      cache_creation_input_tokens, cache_read_input_tokens, eur, estimado}], total_eur }
//   `regla` null = fila anterior a GEN-MO-017 (sin registro). Importes como cadena decimal.
//   `clase` (GEN-MO-011) = clase de tarea que el kernel reconoció en el mensaje del turno; null si ninguna.
export async function getLlamadasTurno(messageId, { fecha } = {}) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return {
      message_id: messageId,
      llamadas: [
        { paso: 'juez_rt02', regla: 'juez', clasificacion_origen: 'dktec', modelo: 'claude-haiku-4-5', input_tokens: 1000, output_tokens: 100, cache_creation_input_tokens: 0, cache_read_input_tokens: 0, eur: '0.001380', estimado: false },
        { paso: 'turno', regla: 'agente_defecto', clasificacion_origen: 'personalizada', modelo: 'claude-sonnet-4-6', input_tokens: 20000, output_tokens: 1000, cache_creation_input_tokens: 0, cache_read_input_tokens: 0, eur: '0.069000', estimado: false },
        { paso: 'juez_rt08', regla: null, clasificacion_origen: null, modelo: 'claude-opus-4-8', input_tokens: 2000, output_tokens: 200, cache_creation_input_tokens: 0, cache_read_input_tokens: 0, eur: '0.013800', estimado: false },
      ],
      total_eur: '0.084180',
    }
  }
  const q = fecha ? `?fecha=${encodeURIComponent(fecha)}` : ''
  const res = checkAuth(
    await fetch(`${apiBase()}/admin/turnos/${encodeURIComponent(messageId)}/llamadas${q}`, { headers: authHeaders(false) }),
    'getLlamadasTurno',
  )
  if (!res.ok) throw await apiError(res, 'getLlamadasTurno')
  return res.json()
}

// GET /v1/admin/chats/{chat_id}/coste — GEN-MO-024: el cuadro de coste de TODO el hilo (solo admin; la
// puerta vive en el kernel). Una fila por (paso, modelo, regla); los turnos fallidos salen en una fila
// «turnos fallidos» y las filas sin paso en «sin desglose». Importes como cadena decimal.
// -> { chat_id, filas: [{paso, regla, clasificacion_origen, modelo, input_tokens, output_tokens,
//      cache_creation_input_tokens, cache_read_input_tokens, eur, estimado, fallido}], total_eur, estimado,
//      sesion: {resume, nueva, fallback}, por_turno: [{message_id, eur, acumulado_eur}], turnos, truncado }
export async function getCosteHilo(chatId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return {
      chat_id: chatId,
      filas: [
        { paso: 'juez_rt02', regla: 'juez', clasificacion_origen: 'dktec', modelo: 'claude-haiku-4-5', input_tokens: 3000, output_tokens: 300, cache_creation_input_tokens: 0, cache_read_input_tokens: 0, eur: '0.004140', estimado: false, fallido: false },
        { paso: 'turno', regla: 'agente_defecto', clasificacion_origen: null, modelo: 'claude-sonnet-4-6', input_tokens: 40000, output_tokens: 2000, cache_creation_input_tokens: 4000, cache_read_input_tokens: 30000, eur: '0.180000', estimado: false, fallido: false },
        { paso: 'resumen_hilo', regla: null, clasificacion_origen: null, modelo: 'claude-opus-4-8', input_tokens: 1500, output_tokens: 300, cache_creation_input_tokens: 0, cache_read_input_tokens: 0, eur: '0.013800', estimado: false, fallido: false },
      ],
      total_eur: '0.197940',
      estimado: false,
      sesion: { resume: 2, nueva: 1, fallback: 0 },
      por_turno: [],
      turnos: 3,
      truncado: false,
    }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/admin/chats/${encodeURIComponent(chatId)}/coste`, { headers: authHeaders(false) }),
    'getCosteHilo',
  )
  if (!res.ok) throw await apiError(res, 'getCosteHilo')
  return res.json()
}

// GET /v1/agentes/permisos — GEN-TL-008: vista agente x herramienta sobre las fichas (solo admin).
// -> { agentes: [{id, nombre, asignacion: {para_que, por:{id,nombre}, cuando}|null,
//      herramientas: [{recurso, nombre, descripcion}]}],
//      catalogo: [{recurso, nombre, descripcion}] }
const MOCK_CATALOGO = [
  { recurso: 'qdrant_dktec_knowledge', nombre: 'Memoria de conocimiento (consulta)', descripcion: 'Busca en la memoria vectorial de conocimiento de DKtec.' },
  { recurso: 'buscar_web', nombre: 'Buscar en la web (consulta)', descripcion: 'Busca en la web información actual.' },
]
const MOCK_PERMISOS = [
  { id: 'anne', nombre: 'Anne',
    asignacion: { para_que: 'Siembra: conserva lo que ya tenia', por: { id: 'migracion', nombre: 'Migracion GEN-TL-008' }, cuando: '2026-10-27T00:00:00+00:00' },
    herramientas: [MOCK_CATALOGO[0]] },
  { id: 'seo', nombre: 'Seo', asignacion: null, herramientas: [] },
]

export async function getPermisosAgentes() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return { agentes: MOCK_PERMISOS, catalogo: MOCK_CATALOGO }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes/permisos`, { headers: authHeaders(false) }),
    'getPermisosAgentes',
  )
  if (!res.ok) throw await apiError(res, 'getPermisosAgentes')
  return res.json()
}

// PUT /v1/agentes/{id}/herramientas — GEN-TL-008, vía provisional del administrador:
// fija las herramientas de la ficha del agente (solo admin). `para_que` obligatorio.
// Vale desde el siguiente turno del agente. Sustituye a PUT /permiso.
export async function asignarHerramientas(agenteId, { herramientas, para_que }) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    const ag = MOCK_PERMISOS.find(a => a.id === agenteId)
    if (!ag) throw new ApiError('no_encontrado', 'agente desconocido', 404)
    ag.herramientas = MOCK_CATALOGO.filter(c => herramientas.includes(c.recurso))
    ag.asignacion = { para_que, por: { id: 'mock', nombre: 'Tú' }, cuando: new Date().toISOString() }
    return { id: agenteId, herramientas, asignacion: ag.asignacion }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes/${encodeURIComponent(agenteId)}/herramientas`, {
      method: 'PUT',
      headers: authHeaders(true),
      body: JSON.stringify({ herramientas, para_que }),
    }),
    'asignarHerramientas',
  )
  if (!res.ok) throw await apiError(res, 'asignarHerramientas')
  return res.json()
}

// GET /v1/agentes — GEN-AG-001: las fichas de agente del tenant, parte en
// vigor (solo admin; la puerta vive en el kernel).
// -> { agentes: [{id, version, nombre, departamento: {nombre, lidera}, estado, foto, miniatura, ...}] }
const MOCK_AGENTES = [
  { id: 'anne', version: 1, nombre: 'Anne', perfil: '', ambito: '', caracter: '', comunicacion: '',
    departamento: { nombre: 'Dirección', lidera: false }, estado: 'activo', foto: null, miniatura: null },
  { id: 'jimmy', version: 1, nombre: 'Jimmy', perfil: '', ambito: '', caracter: '', comunicacion: '',
    departamento: { nombre: 'DIKW Office', lidera: false }, estado: 'inactivo', foto: null, miniatura: null },
]

// Propuestas del mock por id. jimmy trae una para ver el aviso y la comparación.
const MOCK_PROPUESTAS = {
  jimmy: { ...MOCK_AGENTES[1], version: 2, perfil: 'conocimiento', comunicacion: 'Breve y con fuentes' },
}
delete MOCK_PROPUESTAS.jimmy.id

export async function listAgentes() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return { agentes: MOCK_AGENTES }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes`, { headers: authHeaders(false) }),
    'listAgentes',
  )
  if (!res.ok) throw await apiError(res, 'listAgentes')
  return res.json()
}

// GET /v1/agentes/{id} — GEN-AG-002: la ficha completa (solo admin).
// -> { id, vigor: {...}, propuesta: {...} | null }. 404 si no hay ficha.
export async function getAgente(agenteId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    const { id, ...vigor } = MOCK_AGENTES.find(a => a.id === agenteId) ?? {}
    if (!id) throw new ApiError('no_encontrado', 'agente desconocido', 404)
    return { id, vigor, propuesta: MOCK_PROPUESTAS[id] ?? null }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes/${encodeURIComponent(agenteId)}`, { headers: authHeaders(false) }),
    'getAgente',
  )
  if (!res.ok) throw await apiError(res, 'getAgente')
  return res.json()
}

// POST /v1/agentes — GEN-AG-007: da de alta un agente (nace inactivo). Cuerpo
// {id, nombre, departamento, perfil?, ambito?, caracter?, comunicacion?}; los
// textos, si vienen, quedan como propuesta. -> { id, vigor, propuesta }.
// 409 ya_existe si el id está ocupado.
export async function crearAgente(datos) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    if (MOCK_AGENTES.some(a => a.id === datos.id)) throw new ApiError('ya_existe', 'ya hay un agente con ese id', 409)
    const vigor = { version: 1, nombre: datos.nombre, cargo: '', perfil: '', ambito: '', instrucciones: '', caracter: '', comunicacion: '',
      departamento: { nombre: datos.departamento, lidera: false }, estado: 'inactivo', foto: null, miniatura: null }
    MOCK_AGENTES.push({ id: datos.id, ...vigor })
    return { id: datos.id, vigor, propuesta: null }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify(datos),
    }),
    'crearAgente',
  )
  if (!res.ok) throw await apiError(res, 'crearAgente')
  return res.json()
}

// PUT /v1/agentes/{id}/propuesta — GEN-AG-003: guarda los textos como
// propuesta (vigor no cambia). cambios = {perfil, ambito, caracter,
// comunicacion, departamento: {nombre, lidera}} -> { id, vigor, propuesta }
export async function guardarPropuesta(agenteId, cambios) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    const { id, ...vigor } = MOCK_AGENTES.find(a => a.id === agenteId) ?? {}
    if (!id) throw new ApiError('no_encontrado', 'agente desconocido', 404)
    const base = MOCK_PROPUESTAS[id] ?? { ...vigor, version: vigor.version + 1 }
    MOCK_PROPUESTAS[id] = { ...base, ...cambios, nombre: vigor.nombre, estado: vigor.estado }
    return { id, vigor, propuesta: MOCK_PROPUESTAS[id] }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes/${encodeURIComponent(agenteId)}/propuesta`, {
      method: 'PUT',
      headers: authHeaders(true),
      body: JSON.stringify(cambios),
    }),
    'guardarPropuesta',
  )
  if (!res.ok) throw await apiError(res, 'guardarPropuesta')
  return res.json()
}

// POST /v1/agentes/{id}/imagen?tipo=foto|miniatura — GEN-AG-004: sube la
// imagen COMO PROPUESTA (vigor no cambia). Cuerpo crudo, JPEG o PNG, <= 3 MB.
// -> { id, vigor, propuesta, fichero }. 415 otro tipo, 413 demasiado grande.
const MOCK_IMAGENES = {}

// Tamaño máximo de foto y miniatura: el mismo que impone el kernel (413 si se
// supera). El front valida antes de subir para no enviar lo que se va a rechazar.
export const MAX_IMAGEN_BYTES = 3 * 1024 * 1024

export async function subirImagenAgente(agenteId, tipo, file) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      throw new ApiError('tipo_no_admitido', 'solo image/jpeg o image/png', 415)
    }
    if (file.size > MAX_IMAGEN_BYTES) throw new ApiError('imagen_demasiado_grande', 'la imagen supera 3 MB', 413)
    const { id, ...vigor } = MOCK_AGENTES.find(a => a.id === agenteId) ?? {}
    if (!id) throw new ApiError('no_encontrado', 'agente desconocido', 404)
    const n = Object.keys(MOCK_IMAGENES).filter(k => k.startsWith(`${id}/${tipo}-v`)).length + 1
    const fichero = `${tipo}-v${n}.${file.type === 'image/png' ? 'png' : 'jpg'}`
    MOCK_IMAGENES[`${id}/${fichero}`] = file
    const base = MOCK_PROPUESTAS[id] ?? { ...vigor, version: vigor.version + 1 }
    MOCK_PROPUESTAS[id] = { ...base, [tipo]: fichero }
    return { id, vigor, propuesta: MOCK_PROPUESTAS[id], fichero }
  }
  const q = new URLSearchParams({ tipo })
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes/${encodeURIComponent(agenteId)}/imagen?${q}`, {
      method: 'POST',
      headers: { ...authHeaders(false), 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    }),
    'subirImagenAgente',
  )
  if (!res.ok) throw await apiError(res, 'subirImagenAgente')
  return res.json()
}

// POST /v1/agentes/{id}/poner-en-vigor — GEN-AG-005: vigor = propuesta,
// propuesta = null. El kernel la aplica desde el siguiente turno.
// -> { id, vigor, propuesta: null }. 409 si no hay propuesta.
export async function ponerEnVigor(agenteId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    const agente = MOCK_AGENTES.find(a => a.id === agenteId)
    if (!agente) throw new ApiError('no_encontrado', 'agente desconocido', 404)
    const propuesta = MOCK_PROPUESTAS[agenteId]
    if (!propuesta) throw new ApiError('sin_propuesta', 'no hay propuesta que poner en vigor', 409)
    Object.assign(agente, propuesta)
    delete MOCK_PROPUESTAS[agenteId]
    const { id, ...vigor } = agente
    return { id, vigor, propuesta: null }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes/${encodeURIComponent(agenteId)}/poner-en-vigor`, {
      method: 'POST',
      headers: authHeaders(false),
    }),
    'ponerEnVigor',
  )
  if (!res.ok) throw await apiError(res, 'ponerEnVigor')
  return res.json()
}

// POST /v1/agentes/{id}/estado — GEN-AG-008: activa ('activo') o desactiva
// ('inactivo') al agente; acto directo del admin, sin propuesta.
// -> { id, vigor, propuesta|null }
export async function cambiarEstadoAgente(agenteId, estado) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    const agente = MOCK_AGENTES.find(a => a.id === agenteId)
    if (!agente) throw new ApiError('no_encontrado', 'agente desconocido', 404)
    agente.estado = estado
    if (MOCK_PROPUESTAS[agenteId]) MOCK_PROPUESTAS[agenteId].estado = estado
    const { id, ...vigor } = agente
    return { id, vigor, propuesta: MOCK_PROPUESTAS[agenteId] ?? null }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes/${encodeURIComponent(agenteId)}/estado`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ estado }),
    }),
    'cambiarEstadoAgente',
  )
  if (!res.ok) throw await apiError(res, 'cambiarEstadoAgente')
  return res.json()
}

// GEN-AG-006: pide una imagen por la RUTA que da el kernel (avatar_url de
// agents_visibles, p. ej. `/v1/agentes/anne/imagen/foto-v1.jpg`). Con API_BASE
// absoluto (producción) se resuelve contra su origen; con `/v1` (dev, proxy de
// vite) la ruta ya vale. -> Blob, o null en mock (iniciales).
export async function getImagenKernel(ruta) {
  if (USE_MOCK) {
    // Solo las fotos ficticias de los escenarios de desarrollo (src/mock/descriptor.js).
    if (!ruta.startsWith('/mock-fotos/')) return null
    const res = await fetch(ruta)
    return res.ok ? res.blob() : null
  }
  const url = /^https?:\/\//.test(apiBase()) ? new URL(ruta, apiBase()).href : ruta
  const res = checkAuth(await fetch(url, { headers: authHeaders(false) }), 'getImagenKernel')
  if (!res.ok) throw await apiError(res, 'getImagenKernel')
  return res.blob()
}

// GET /v1/agentes/{id}/imagen/{nombre} -> Blob. Va por fetch (con Bearer) y no
// por <img src>, porque la imagen está tras la puerta de admin del kernel.
export async function getAgenteImagen(agenteId, nombre) {
  if (USE_MOCK) {
    const blob = MOCK_IMAGENES[`${agenteId}/${nombre}`]
    if (!blob) throw new ApiError('no_encontrado', 'sin imágenes en mock', 404)
    return blob
  }
  const res = checkAuth(
    await fetch(
      `${apiBase()}/agentes/${encodeURIComponent(agenteId)}/imagen/${encodeURIComponent(nombre)}`,
      { headers: authHeaders(false) },
    ),
    'getAgenteImagen',
  )
  if (!res.ok) throw await apiError(res, 'getAgenteImagen')
  return res.blob()
}

// ===========================================================================
// Visor de ficheros de la ZFT — GEN-22 #323 (eje A del owner)
// Renderizador fino (ADR-020 §2): el front NO toca S3; pide listar/subir/
// descargar por endpoints y ORDENA la lectura de `raw` por el flujo tutelado
// (proposeFileAction -> approveAction). La puerta del eje A y el grant de `raw`
// viven en el kernel (zft_api.py); el front solo pinta y mapea errores.
// ---------------------------------------------------------------------------
// GET /v1/files/{zona}?path= -> { zona, path, vista, entries:[...] }
// ---------------------------------------------------------------------------
export async function listFiles(zona, path = '') {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return listFilesMock(zona, path)
  }
  const q = new URLSearchParams()
  if (path) q.set('path', path)
  const res = checkAuth(
    await fetch(`${apiBase()}/files/${encodeURIComponent(zona)}?${q}`, { headers: authHeaders(false) }),
    'listFiles',
  )
  if (!res.ok) throw await apiError(res, 'listFiles')
  return res.json()
}

// ---------------------------------------------------------------------------
// POST /v1/files/{zona}?filename=&path= (cuerpo = fichero crudo) — subir a Inbox
// ---------------------------------------------------------------------------
export async function uploadInboxFile(zona, file, path = '') {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 300))
    const content = file.text ? await file.text() : ''
    return uploadInboxMock(zona, file.name, path, content)
  }
  const q = new URLSearchParams({ filename: file.name })
  if (path) q.set('path', path)
  const res = checkAuth(
    await fetch(`${apiBase()}/files/${encodeURIComponent(zona)}?${q}`, {
      method: 'POST',
      headers: { ...authHeaders(false), 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    }),
    'uploadInboxFile',
  )
  if (!res.ok) throw await apiError(res, 'uploadInboxFile')
  return res.json()
}

// ---------------------------------------------------------------------------
// GET /v1/files/{zona}/content?path=&approval= -> { blob, filename }
// outputs/archive: descarga directa. raw: SOLO con `approval` de una acción
// `leer_raw` aprobada (el kernel valida el grant; sin él, 403).
// ---------------------------------------------------------------------------
export async function downloadFile(zona, path, approval = null) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    if (zona === 'raw' && !rawGrant(approval, path)) {
      throw new ApiError('requiere_accion_tutelada', 'leer «raw» exige una acción tutelada aprobada', 403)
    }
    const meta = readFileMock(zona, path)
    const blob = new Blob([meta.content], { type: meta.contentType })
    return { blob, filename: path.split('/').pop() }
  }
  const q = new URLSearchParams({ path })
  if (approval) q.set('approval', approval)
  const res = checkAuth(
    await fetch(`${apiBase()}/files/${encodeURIComponent(zona)}/content?${q}`, { headers: authHeaders(false) }),
    'downloadFile',
  )
  if (!res.ok) throw await apiError(res, 'downloadFile')
  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') || ''
  const m = /filename="?([^"]+)"?/.exec(cd)
  return { blob, filename: m ? m[1] : path.split('/').pop() }
}

// ---------------------------------------------------------------------------
// POST /v1/files/actions — PROPONE una acción de fichero (hoy `leer_raw`).
// Devuelve el action_preview; la APROBACIÓN reusa approveAction (contrato
// AGF2-07, misma puerta A2). No es un contrato nuevo.
// ---------------------------------------------------------------------------
export async function proposeFileAction(actionType, params = {}) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 250))
    return proposeMockAction('zft-ficheros', actionType, params)
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/files/actions`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ action_type: actionType, params }),
    }),
    'proposeFileAction',
  )
  if (!res.ok) throw await apiError(res, 'proposeFileAction')
  return res.json()
}

// ---------------------------------------------------------------------------
// GET /v1/files/policy — política de gobierno de la ZFT (#322): taxonomía
// sugerida + retención declarativa. VACÍA hoy (la rellenan Cassie/Max por
// config); el front la consume para pintar pistas sin tocar código.
// ---------------------------------------------------------------------------
export async function getFilePolicy() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 150))
    return getFilePolicyMock()
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/files/policy`, { headers: authHeaders(false) }),
    'getFilePolicy',
  )
  if (!res.ok) throw await apiError(res, 'getFilePolicy')
  return res.json()
}

// ===========================================================================
// Visor de mecanismo (GEN-76 T7) — seis rutas de lectura + la propuesta.
// El visor NO conoce ningún mecanismo: todo lo que pinta sale del descriptor
// que sirve el kernel. La APROBACIÓN reusa `approveAction` (misma puerta A2):
// no hay contrato nuevo y `contract_version` no sube.
// ===========================================================================

export async function listMecanismos() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 120))
    return listMecanismosMock()
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/mecanismos`, { headers: authHeaders(false) }),
    'listMecanismos',
  )
  if (!res.ok) throw await apiError(res, 'listMecanismos')
  return res.json()
}

export async function getMecanismoEsquema(codigo) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 120))
    return getEsquemaMock(codigo)
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/mecanismos/${encodeURIComponent(codigo)}/esquema`, {
      headers: authHeaders(false),
    }),
    'getMecanismoEsquema',
  )
  if (!res.ok) throw await apiError(res, 'getMecanismoEsquema')
  return res.json()
}

export async function listMecanismoObjetos(codigo, { q, filtros, pagina, tam, orden, sentido } = {}) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 120))
    return listObjetosMock(codigo, { q, filtros, pagina, tam })
  }
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  // Los filtros viajan como `eje:valor` separados por comas: el kernel los
  // valida contra los ejes DECLARADOS y sus enumerados. El front no inventa.
  const pares = Object.entries(filtros ?? {}).filter(([, v]) => v)
  if (pares.length) params.set('filtros', pares.map(([k, v]) => `${k}:${v}`).join(','))
  if (pagina) params.set('pagina', String(pagina))
  if (tam) params.set('tam', String(tam))
  if (orden) params.set('orden', orden)
  if (sentido) params.set('sentido', sentido)
  const res = checkAuth(
    await fetch(`${apiBase()}/mecanismos/${encodeURIComponent(codigo)}/objetos?${params}`, {
      headers: authHeaders(false),
    }),
    'listMecanismoObjetos',
  )
  if (!res.ok) throw await apiError(res, 'listMecanismoObjetos')
  return res.json()
}

export async function getMecanismoObjeto(codigo, clave) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 120))
    return getObjetoMock(codigo, clave)
  }
  const res = checkAuth(
    await fetch(
      `${apiBase()}/mecanismos/${encodeURIComponent(codigo)}/objetos/${encodeURIComponent(clave)}`,
      { headers: authHeaders(false) },
    ),
    'getMecanismoObjeto',
  )
  if (!res.ok) throw await apiError(res, 'getMecanismoObjeto')
  return res.json()
}

export async function getMecanismoVersiones(codigo, clave) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 120))
    return getVersionesMock(codigo, clave)
  }
  const res = checkAuth(
    await fetch(
      `${apiBase()}/mecanismos/${encodeURIComponent(codigo)}/objetos/${encodeURIComponent(clave)}/versiones`,
      { headers: authHeaders(false) },
    ),
    'getMecanismoVersiones',
  )
  if (!res.ok) throw await apiError(res, 'getMecanismoVersiones')
  return res.json()
}

export async function getMecanismoRegistros(codigo, clave, registro) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 120))
    return getRegistrosMock(codigo, clave, registro)
  }
  const res = checkAuth(
    await fetch(
      `${apiBase()}/mecanismos/${encodeURIComponent(codigo)}/objetos/${encodeURIComponent(clave)}/registros/${encodeURIComponent(registro)}`,
      { headers: authHeaders(false) },
    ),
    'getMecanismoRegistros',
  )
  if (!res.ok) throw await apiError(res, 'getMecanismoRegistros')
  return res.json()
}

// POST /v1/mecanismos/{codigo}/acciones — PROPONE (A1+A2). El efecto NO ocurre
// aquí: se aprueba con `approveAction`, la puerta de siempre.
export async function proposeMecanismoAction(codigo, accionId, params = {}) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return proponerAccionMock(codigo, accionId, params)
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/mecanismos/${encodeURIComponent(codigo)}/acciones`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ action_type: accionId, params }),
    }),
    'proposeMecanismoAction',
  )
  if (!res.ok) throw await apiError(res, 'proposeMecanismoAction')
  return res.json()
}

// ===========================================================================
// GET /v1/herramientas y GET /v1/herramientas/{id} — GEN-TL-001..005: catálogo
// de herramientas de los agentes (solo admin; la puerta vive en el kernel).
// Motor n8n se ve como UNA herramienta con 8 niveles ordenados de menos a más;
// `pide_confirmacion` lo calcula el kernel contra ACTION_REGISTRY — con los
// datos de hoy solo lo llevan «Proponer objetos de mecanismo» y «Proponer alta
// de agente»; los 8 niveles de Motor n8n NO lo llevan (ver el informe de la
// tarea: la historia da por hecho que «ejecutar flujo» sí lo pide, pero el
// kernel no lo exige todavía).
// -> listHerramientas(): { herramientas: [{id, nombre, descripcion, niveles}] }
// -> getHerramienta(id): { id, nombre, descripcion, actua_sobre,
//      niveles: [{nombre, frase, pide_confirmacion}] } · 404 si no existe
// ---------------------------------------------------------------------------
const MOCK_HERRAMIENTAS = [
  { id: 'odoo', nombre: 'Odoo (lectura y escritura)', descripcion: 'Lee y escribe en el ERP Odoo.',
    actua_sobre: 'El ERP Odoo del tenant.',
    niveles: [{ nombre: 'Lectura y escritura', frase: 'Lee y escribe en el ERP Odoo.', pide_confirmacion: false }] },
  { id: 'postgres_dktec_etl_dml', nombre: 'Base ETL (escritura)', descripcion: 'Modifica datos de la base de datos ETL.',
    actua_sobre: 'La base de datos ETL.',
    niveles: [{ nombre: 'Escritura', frase: 'Modifica datos de la base de datos ETL.', pide_confirmacion: false }] },
  { id: 'postgres_dktec_dml', nombre: 'Base DKtec (escritura)', descripcion: 'Modifica datos de la base de datos de DKtec.',
    actua_sobre: 'La base de datos de DKtec.',
    niveles: [{ nombre: 'Escritura', frase: 'Modifica datos de la base de datos de DKtec.', pide_confirmacion: false }] },
  { id: 'postgres_select', nombre: 'Bases de datos (consulta)', descripcion: 'Consulta las bases de datos sin modificarlas.',
    actua_sobre: 'Las bases de datos del tenant (solo lectura).',
    niveles: [{ nombre: 'Consulta', frase: 'Consulta las bases de datos sin modificarlas.', pide_confirmacion: false }] },
  { id: 'postgres_ddl', nombre: 'Bases de datos (estructura)', descripcion: 'Crea y cambia tablas y esquemas.',
    actua_sobre: 'Las tablas y esquemas de las bases de datos.',
    niveles: [{ nombre: 'Estructura', frase: 'Crea y cambia tablas y esquemas.', pide_confirmacion: false }] },
  { id: 'ekb_escritura', nombre: 'Base de conocimiento 04 EKB (escritura)', descripcion: 'Escribe en la carpeta 04 EKB.',
    actua_sobre: 'La carpeta 04 EKB de la base de conocimiento.',
    niveles: [{ nombre: 'Escritura', frase: 'Escribe en la carpeta 04 EKB.', pide_confirmacion: false }] },
  { id: 'qdrant_dktec_financial', nombre: 'Memoria financiera (consulta)', descripcion: 'Busca en la memoria vectorial financiera.',
    actua_sobre: 'La memoria vectorial financiera.',
    niveles: [{ nombre: 'Consulta', frase: 'Busca en la memoria vectorial financiera.', pide_confirmacion: false }] },
  { id: 'qdrant_dktec_knowledge', nombre: 'Memoria de conocimiento (consulta)', descripcion: 'Busca en la memoria vectorial de conocimiento de DKtec.',
    actua_sobre: 'La memoria vectorial de conocimiento de DKtec.',
    niveles: [{ nombre: 'Consulta', frase: 'Busca en la memoria vectorial de conocimiento de DKtec.', pide_confirmacion: false }] },
  { id: 'mecanismo_proponer', nombre: 'Proponer objetos de mecanismo (propuesta)', descripcion: 'Propone desde el chat el alta de una norma u otro objeto de mecanismo.',
    actua_sobre: 'Los objetos gobernados de un mecanismo (p. ej. normas).',
    niveles: [{ nombre: 'Propuesta', frase: 'Propone desde el chat el alta de una norma u otro objeto de mecanismo.', pide_confirmacion: true }] },
  { id: 'leer_documento', nombre: 'Leer documento (consulta)', descripcion: 'Lee un documento por su nombre en las zonas de ficheros del tenant.',
    actua_sobre: 'Los documentos del tenant, por su nombre.',
    niveles: [{ nombre: 'Consulta', frase: 'Lee un documento por su nombre en las zonas de ficheros del tenant.', pide_confirmacion: false }] },
  { id: 'buscar_documentos', nombre: 'Buscar documentos (consulta)', descripcion: 'Busca un texto en el nombre y el contenido de los documentos del tenant.',
    actua_sobre: 'Los documentos del tenant, por nombre y contenido.',
    niveles: [{ nombre: 'Consulta', frase: 'Busca un texto en el nombre y el contenido de los documentos del tenant.', pide_confirmacion: false }] },
  { id: 'espacio_trabajo', nombre: 'Espacio de trabajo (ejecución y edición)', descripcion: 'Ejecuta comandos y crea, edita y lee ficheros en un espacio de trabajo propio del hilo, sin acceso a la red.',
    actua_sobre: 'Un espacio de trabajo propio del hilo, sin red.',
    niveles: [{ nombre: 'Ejecución y edición', frase: 'Ejecuta comandos y crea, edita y lee ficheros en un espacio de trabajo propio del hilo, sin acceso a la red.', pide_confirmacion: false }] },
  { id: 'buscar_web', nombre: 'Buscar en la web (consulta)', descripcion: 'Busca en la web información actual que no está en los documentos del tenant.',
    actua_sobre: 'La web pública.',
    niveles: [{ nombre: 'Consulta', frase: 'Busca en la web información actual que no está en los documentos del tenant.', pide_confirmacion: false }] },
  { id: 'leer_pagina', nombre: 'Leer una página web (consulta)', descripcion: 'Lee una página web por su dirección. Lo que trae es material externo, sin autoridad.',
    actua_sobre: 'Una página web, por su dirección.',
    niveles: [{ nombre: 'Consulta', frase: 'Lee una página web por su dirección. Lo que trae es material externo, sin autoridad.', pide_confirmacion: false }] },
  { id: 'agente_proponer_alta', nombre: 'Proponer alta de agente (propuesta)', descripcion: 'Propone desde el chat la ficha de un agente nuevo.',
    actua_sobre: 'Las fichas de agente de «Mi equipo».',
    niveles: [{ nombre: 'Propuesta', frase: 'Propone desde el chat la ficha de un agente nuevo.', pide_confirmacion: true }] },
  { id: 'n8n', nombre: 'Motor n8n',
    descripcion: 'Usa el motor de automatización n8n del tenant: listar, leer, ejecutar, crear, cambiar, activar, desactivar y borrar flujos.',
    actua_sobre: 'El motor de automatización n8n del tenant.',
    niveles: [
      { nombre: 'Listar flujos', frase: 'Lista los flujos de automatización del motor n8n del tenant.', pide_confirmacion: false },
      { nombre: 'Leer flujo', frase: 'Lee el detalle de un flujo del motor n8n por su ID.', pide_confirmacion: false },
      { nombre: 'Ejecutar flujo', frase: 'Lanza un flujo del motor n8n por su ID y espera su resultado.', pide_confirmacion: false },
      { nombre: 'Crear flujo', frase: 'Crea un flujo nuevo en el motor n8n. No lo activa.', pide_confirmacion: false },
      { nombre: 'Cambiar flujo', frase: 'Reemplaza por completo un flujo existente del motor n8n por su ID.', pide_confirmacion: false },
      { nombre: 'Activar flujo', frase: 'Activa (publica) un flujo del motor n8n por su ID.', pide_confirmacion: false },
      { nombre: 'Desactivar flujo', frase: 'Detiene un flujo activo del motor n8n por su ID, sin borrarlo.', pide_confirmacion: false },
      { nombre: 'Borrar flujo', frase: 'Borra un flujo del motor n8n por su ID.', pide_confirmacion: false },
    ] },
]

export async function listHerramientas() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return {
      herramientas: MOCK_HERRAMIENTAS.map(h => (
        { id: h.id, nombre: h.nombre, descripcion: h.descripcion, niveles: h.niveles.length }
      )),
    }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/herramientas`, { headers: authHeaders(false) }),
    'listHerramientas',
  )
  if (!res.ok) throw await apiError(res, 'listHerramientas')
  return res.json()
}

export async function getHerramienta(herramientaId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    const h = MOCK_HERRAMIENTAS.find(x => x.id === herramientaId)
    if (!h) throw new ApiError('no_encontrado', 'herramienta desconocida', 404)
    return h
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/herramientas/${encodeURIComponent(herramientaId)}`, { headers: authHeaders(false) }),
    'getHerramienta',
  )
  if (!res.ok) throw await apiError(res, 'getHerramienta')
  return res.json()
}

// GEN-TL-006 — GET /v1/agentes/{id}/herramientas (solo admin, solo lectura):
// las herramientas del catálogo que tiene el agente, de su ficha. Solo salen los niveles concedidos. Sin perfil o sin herramientas -> [].
// -> { id, herramientas: [{id, nombre, niveles: [{clave_recurso, nombre, orden, pide_confirmacion}]}],
//      sin_catalogo: [{recurso, nombre, descripcion}] }
export async function getHerramientasAgente(agenteId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    const permiso = MOCK_PERMISOS.find(a => a.id === agenteId)
    const herramientas = (permiso?.herramientas ?? []).map(h => ({
      id: h.recurso, nombre: h.nombre,
      niveles: [{ clave_recurso: h.recurso, nombre: 'Consulta', orden: 1, pide_confirmacion: false }],
    }))
    return { id: agenteId, herramientas, sin_catalogo: [] }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/agentes/${encodeURIComponent(agenteId)}/herramientas`, { headers: authHeaders(false) }),
    'getHerramientasAgente',
  )
  if (!res.ok) throw await apiError(res, 'getHerramientasAgente')
  return res.json()
}

// GEN-TL-007 — GET /v1/herramientas/{id}/agentes (solo admin, solo lectura): los
// agentes que tienen la herramienta y con qué niveles. Vista inversa de
// getHerramientasAgente: el kernel usa la misma lógica para las dos. Ninguno -> [].
// -> { id, agentes: [{id, nombre, estado, niveles: [{clave_recurso, nombre, orden, pide_confirmacion}]}] }
export async function getAgentesDeHerramienta(herramientaId) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    const agentes = MOCK_PERMISOS
      .filter(a => (a.herramientas ?? []).some(h => h.recurso === herramientaId))
      .map(a => ({
        id: a.id, nombre: a.nombre, estado: 'activo',
        niveles: [{ clave_recurso: herramientaId, nombre: 'Consulta', orden: 1, pide_confirmacion: false }],
      }))
    return { id: herramientaId, agentes }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/herramientas/${encodeURIComponent(herramientaId)}/agentes`, { headers: authHeaders(false) }),
    'getAgentesDeHerramienta',
  )
  if (!res.ok) throw await apiError(res, 'getAgentesDeHerramienta')
  return res.json()
}

// ===========================================================================
// «Mi empresa» · KBs de empresa — GEN-EKB-008
//   GET  /v1/ekb/kbs?estado=activo|retirado|todos → { kbs: [...], titulares_posibles? (solo admin) }
//   POST /v1/ekb/kbs {nombre, descripcion, titular?, etiqueta?, area_id?} → 201 { kb }
//   PUT  /v1/ekb/kbs/{id} {nombre?, descripcion?, titular?} → { kb }
//   POST /v1/ekb/kbs/{id}/retirar → { kb }
//   GET  /v1/ekb/traza → { traza: [{id, ts, actor, accion, contenedor_id, detalle}] }
// Los permisos los revalida el kernel en cada petición; el front solo pinta.
// ===========================================================================
const MOCK_EKB = { kbs: [], traza: [] }
const MOCK_TITULARES = [{ email: 'admin@mock.test', display_name: 'Admin', rol: 'admin' }, { email: 'champion@mock.test', display_name: 'Champion', rol: 'champion' }]

function mockEkb(accion, id, detalle) {
  MOCK_EKB.traza.unshift({ id: MOCK_EKB.traza.length + 1, ts: new Date().toISOString(), actor: 'admin@mock.test', accion, contenedor_id: id, detalle })
}

async function ekbFetch(ruta, nombre, opciones = {}) {
  const res = checkAuth(
    await fetch(`${apiBase()}/ekb${ruta}`, { headers: authHeaders(opciones.body !== undefined), cache: 'no-store', ...opciones }),
    nombre,
  )
  if (!res.ok) throw await apiError(res, nombre)
  return res.json()
}

export async function listKbs(estado = 'activo') {
  if (USE_MOCK) {
    return {
      kbs: MOCK_EKB.kbs.filter(k => estado === 'todos' || k.estado === estado),
      titulares_posibles: MOCK_TITULARES,
    }
  }
  return ekbFetch(`/kbs?estado=${encodeURIComponent(estado)}`, 'listKbs')
}

export async function crearKb({ nombre, descripcion, titular, etiqueta, area_id }) {
  if (USE_MOCK) {
    const email = titular || 'admin@mock.test'
    const kb = {
      id: `kb-${MOCK_EKB.kbs.length + 1}`, nombre, descripcion, clase: 'empresa', etiqueta: etiqueta || 'interna', etiqueta_pendiente: null, estado: 'activo',
      origen: 'propio', area_id: area_id || null, titular: { email, display_name: email }, creado_en: new Date().toISOString(),
    }
    MOCK_EKB.kbs.push(kb)
    mockEkb('kb_creado', kb.id, { nombre, titular: email })
    return { kb }
  }
  return ekbFetch('/kbs', 'crearKb', { method: 'POST', body: JSON.stringify({ nombre, descripcion, titular: titular || null, etiqueta: etiqueta || 'interna', area_id: area_id || null }) })
}

export async function editarKb(id, cambios) {
  if (USE_MOCK) {
    const kb = MOCK_EKB.kbs.find(k => k.id === id)
    const { etiqueta, ...resto } = cambios
    Object.assign(kb, resto)
    mockEkb('kb_editado', id, { nombre: kb.nombre, despues: resto })
    const rango = { publica: 1, interna: 2, confidencial: 3 }
    let resultado
    if (etiqueta && etiqueta !== kb.etiqueta) {
      if (rango[etiqueta] > rango[kb.etiqueta]) {
        mockEkb('kb_etiqueta_cambiada', id, { nombre: kb.nombre, de: kb.etiqueta, a: etiqueta })
        kb.etiqueta = etiqueta; resultado = 'aplicado'
      } else {
        kb.etiqueta_pendiente = { id: `cambio-${id}`, de: kb.etiqueta, a: etiqueta, kb: { id, nombre: kb.nombre }, propuesto_por: { email: 'admin@mock.test', display_name: 'Admin' } }
        mockEkb('kb_etiqueta_propuesta', id, { nombre: kb.nombre, de: kb.etiqueta, a: etiqueta }); resultado = 'pendiente'
      }
    }
    return { kb, ...(resultado ? { etiqueta: { resultado } } : {}) }
  }
  return ekbFetch(`/kbs/${encodeURIComponent(id)}`, 'editarKb', { method: 'PUT', body: JSON.stringify(cambios) })
}

export async function retirarKb(id) {
  if (USE_MOCK) {
    const kb = MOCK_EKB.kbs.find(k => k.id === id)
    kb.estado = 'retirado'
    mockEkb('kb_retirado', id, { nombre: kb.nombre })
    return { kb }
  }
  return ekbFetch(`/kbs/${encodeURIComponent(id)}/retirar`, 'retirarKb', { method: 'POST' })
}

export async function getTrazaEkb() {
  if (USE_MOCK) return { traza: MOCK_EKB.traza }
  return ekbFetch('/traza', 'getTrazaEkb')
}

// Permisos por perfil sobre cada KB — GEN-EKB-013 (solo admin; el kernel filtra además el listado de KBs)
//   GET    /v1/ekb/permisos → { perfiles: [{id, etiqueta}], kbs: [{id, nombre, titular, permisos: [{perfil, para_que, concedido_por, concedido_en}]}] }
//   POST   /v1/ekb/kbs/{id}/permisos {perfil, para_que} → 201 { permiso }
//   DELETE /v1/ekb/kbs/{id}/permisos/{perfil} → { revocado: true }
const MOCK_PERFILES_EKB = [{ id: 'usuario', etiqueta: 'Usuario' }, { id: 'champion', etiqueta: 'Champion' }]

export async function getMapaPermisos() {
  if (USE_MOCK) {
    return {
      perfiles: MOCK_PERFILES_EKB,
      kbs: MOCK_EKB.kbs.filter(k => k.estado === 'activo').map(k => ({ id: k.id, nombre: k.nombre, titular: k.titular, permisos: k.permisos ?? [] })),
    }
  }
  return ekbFetch('/permisos', 'getMapaPermisos')
}

export async function concederPermiso(kbId, perfil, para_que) {
  if (USE_MOCK) {
    const kb = MOCK_EKB.kbs.find(k => k.id === kbId)
    const permiso = { perfil, para_que, concedido_por: { email: 'admin@mock.test', display_name: 'Admin' }, concedido_en: new Date().toISOString() }
    kb.permisos = [...(kb.permisos ?? []), permiso]
    mockEkb('kb_permiso_concedido', kbId, { nombre: kb.nombre, perfil, para_que })
    return { permiso }
  }
  return ekbFetch(`/kbs/${encodeURIComponent(kbId)}/permisos`, 'concederPermiso', { method: 'POST', body: JSON.stringify({ perfil, para_que }) })
}

export async function revocarPermiso(kbId, perfil) {
  if (USE_MOCK) {
    const kb = MOCK_EKB.kbs.find(k => k.id === kbId)
    kb.permisos = (kb.permisos ?? []).filter(p => p.perfil !== perfil)
    mockEkb('kb_permiso_revocado', kbId, { nombre: kb.nombre, perfil })
    return { revocado: true }
  }
  return ekbFetch(`/kbs/${encodeURIComponent(kbId)}/permisos/${encodeURIComponent(perfil)}`, 'revocarPermiso', { method: 'DELETE' })
}

// Áreas — GEN-EKB-017 (lectura admin y champion; todo lo demás solo admin, el kernel revalida)
//   GET  /v1/ekb/areas → { areas: [{id, nombre, descripcion, icono, padre_id, orden, sistema, kbs: [{id, nombre, etiqueta}]}], puede_gestionar }
//   POST /v1/ekb/areas {nombre, descripcion?, icono?, padre_id?} → 201 { area }
//   PUT  /v1/ekb/areas/{id} {nombre?, descripcion?, icono?} → { area }
//   POST /v1/ekb/areas/{id}/mover {padre_id|null} → { area }
//   POST /v1/ekb/areas/{id}/retirar {destino_id?} → { area, kbs_movidos, subareas_movidas }   (con contenido, destino obligatorio)
//   POST /v1/ekb/kbs/{id}/mover-area {area_id} → { kb }
const MOCK_AREAS = [{ id: 'sin', nombre: 'Sin clasificar', descripcion: 'KBs pendientes de colocar en su area', icono: '🗂️', padre_id: null, orden: 9999, sistema: true }]

export async function getAreas() {
  if (USE_MOCK) {
    return {
      areas: MOCK_AREAS.filter(a => a.estado !== 'retirado').map(a => ({ ...a, kbs: MOCK_EKB.kbs.filter(k => (k.area_id ?? 'sin') === a.id && k.estado === 'activo').map(k => ({ id: k.id, nombre: k.nombre, etiqueta: k.etiqueta })) })),
      puede_gestionar: true,
    }
  }
  return ekbFetch('/areas', 'getAreas')
}

// Saber — GEN-EKB-018 (cualquier perfil; el kernel cuenta solo lo que quien mira puede ver)
//   GET /v1/ekb/saber → { visible, kbs, areas, ikus, rol, mapa: [{id, nombre, icono, padre_id, ruta, subareas, ikus, kbs_total, kbs: [{id, nombre, etiqueta, titular, ikus, caducan, caducan_desde, puede_editar}]}] } (GEN-EKB-020: puede_editar = puede dar de alta iKus ahí; GEN-EKB-019: `mapa` sale del mismo conjunto de KBs que las cifras)
export async function getSaber() {
  if (USE_MOCK) {
    // GEN-PX-022: un perfil no admin del mock no tiene ningún KB permitido (estado vacío, y ningún KB).
    const rolMock = perfilMockPedido()
    if (rolMock) return { visible: true, kbs: 0, areas: 0, ikus: 0, rol: rolMock, mapa: [] }
    const kbs = MOCK_EKB.kbs.filter(k => k.estado === 'activo')
    return { visible: true, kbs: kbs.length, areas: new Set(kbs.map(k => k.area_id ?? 'sin')).size, ikus: 0, rol: 'admin', mapa: [] }
  }
  return ekbFetch('/saber', 'getSaber')
}

// GEN-PX-032: abrir la página de un área cuenta como uso suyo (POST /v1/ekb/areas/{id}/uso → {usado: true}); el kernel ordena el mapa.
export async function registrarUsoArea(id) {
  if (USE_MOCK) return { usado: true }
  return ekbFetch(`/areas/${encodeURIComponent(id)}/uso`, 'registrarUsoArea', { method: 'POST' })
}

export async function crearArea({ nombre, descripcion, icono, padre_id }) {
  if (USE_MOCK) {
    const area = { id: `area-${MOCK_AREAS.length + 1}`, nombre, descripcion: descripcion || '', icono: icono || '📁', padre_id: padre_id || null, orden: MOCK_AREAS.length, sistema: false }
    MOCK_AREAS.push(area)
    mockEkb('area_creada', null, { area_id: area.id, nombre, padre_id: area.padre_id })
    return { area }
  }
  return ekbFetch('/areas', 'crearArea', { method: 'POST', body: JSON.stringify({ nombre, descripcion: descripcion || '', icono: icono || null, padre_id: padre_id || null }) })
}

export async function editarArea(id, cambios) {
  if (USE_MOCK) {
    const area = MOCK_AREAS.find(a => a.id === id)
    Object.assign(area, cambios)
    mockEkb(cambios.nombre ? 'area_renombrada' : 'area_editada', null, { area_id: id, nombre: area.nombre })
    return { area }
  }
  return ekbFetch(`/areas/${encodeURIComponent(id)}`, 'editarArea', { method: 'PUT', body: JSON.stringify(cambios) })
}

export async function moverArea(id, padre_id) {
  if (USE_MOCK) {
    const area = MOCK_AREAS.find(a => a.id === id)
    area.padre_id = padre_id || null
    mockEkb('area_movida', null, { area_id: id, nombre: area.nombre, a: area.padre_id })
    return { area }
  }
  return ekbFetch(`/areas/${encodeURIComponent(id)}/mover`, 'moverArea', { method: 'POST', body: JSON.stringify({ padre_id: padre_id || null }) })
}

export async function retirarArea(id, destino_id) {
  if (USE_MOCK) {
    const area = MOCK_AREAS.find(a => a.id === id)
    area.estado = 'retirado'
    MOCK_EKB.kbs.filter(k => (k.area_id ?? 'sin') === id).forEach(k => { k.area_id = destino_id || 'sin' })
    MOCK_AREAS.filter(a => a.padre_id === id).forEach(a => { a.padre_id = destino_id || null })
    mockEkb('area_retirada', null, { area_id: id, nombre: area.nombre, destino_id })
    return { area, kbs_movidos: 0, subareas_movidas: 0 }
  }
  return ekbFetch(`/areas/${encodeURIComponent(id)}/retirar`, 'retirarArea', { method: 'POST', body: JSON.stringify({ destino_id: destino_id || null }) })
}

export async function moverKbDeArea(kbId, area_id) {
  if (USE_MOCK) {
    const kb = MOCK_EKB.kbs.find(k => k.id === kbId)
    kb.area_id = area_id
    mockEkb('kb_movido_de_area', kbId, { nombre: kb.nombre, a: area_id, area: MOCK_AREAS.find(a => a.id === area_id)?.nombre })
    return { kb }
  }
  return ekbFetch(`/kbs/${encodeURIComponent(kbId)}/mover-area`, 'moverKbDeArea', { method: 'POST', body: JSON.stringify({ area_id }) })
}

//   GET  /v1/ekb/pendientes → { pendientes: [{id, de, a, kb:{id,nombre}, propuesto_por, propuesto_en}] } (rebajas que espera quien mira)
//   POST /v1/ekb/cambios-etiqueta/{id}/confirmar | /rechazar → { cambio, kb }   (solo el titular del KB)
export async function listPendientes() {
  if (USE_MOCK) return { pendientes: MOCK_EKB.kbs.filter(k => k.etiqueta_pendiente).map(k => k.etiqueta_pendiente), quitas: [] }
  return ekbFetch('/pendientes', 'listPendientes')
}

export async function resolverCambioEtiqueta(id, confirmar) {
  if (USE_MOCK) {
    const kb = MOCK_EKB.kbs.find(k => k.etiqueta_pendiente?.id === id)
    const c = kb.etiqueta_pendiente
    if (confirmar) kb.etiqueta = c.a
    kb.etiqueta_pendiente = null
    mockEkb(confirmar ? 'kb_etiqueta_confirmada' : 'kb_etiqueta_rechazada', kb.id, { nombre: kb.nombre, de: c.de, a: c.a })
    return { kb }
  }
  return ekbFetch(`/cambios-etiqueta/${encodeURIComponent(id)}/${confirmar ? 'confirmar' : 'rechazar'}`, 'resolverCambioEtiqueta', { method: 'POST' })
}

//   GET  /v1/ekb/mis-pendientes → { pendientes: [{id, tipo, resumen, payload, kb?, solicitante:{email, display_name}, agente, estado, creado_en}] }   (GEN-EKB-024)
//        lo que Jimmy preparó por encargo de otra persona y espera MI confirmación (admin y champion)
//   POST /v1/ekb/mis-pendientes/{id}/confirmar | /rechazar → { pendiente, resultado: 'confirmado'|'rechazado', efecto? }   (confirmar ejecuta con MI identidad)
export async function listMisPendientes() {
  if (USE_MOCK) return { pendientes: [] }
  return ekbFetch('/mis-pendientes', 'listMisPendientes')
}

//   GET  /v1/ekb/encargos → { encargos: [{id, estado:'en_curso'|'terminado'|'interrumpido', titulo, solicitante:{email, display_name}, kb:{id, nombre}|null,
//        kbs_total, ikus, ikus_estimados?, avance?(0..1), partes_hechas, partes_total, ficheros, inicio, fin, dudas?, fallidos?}] }   (GEN-EKB-029)
//        GEN-PX-033: los `limite` (3 por defecto; «Historial» pide 100) encargos de aprendizaje más recientes, sin límite de fecha, más `total`
//        con todos los visibles (admin: todos; el resto: los suyos)
export async function listEncargos({ todos = false } = {}) {
  if (USE_MOCK) return { encargos: [], total: 0 }
  return ekbFetch(todos ? '/encargos?limite=100' : '/encargos', 'listEncargos')
}

export async function resolverPendiente(id, confirmar) {
  if (USE_MOCK) return { pendiente: { id, estado: confirmar ? 'confirmado' : 'rechazado' }, resultado: confirmar ? 'confirmado' : 'rechazado' }
  return ekbFetch(`/mis-pendientes/${encodeURIComponent(id)}/${confirmar ? 'confirmar' : 'rechazar'}`, 'resolverPendiente', { method: 'POST' })
}

// ---------------------------------------------------------------------------
// iKus de un KB — GEN-EKB-009 (alta manual, edición y versiones)
//   GET  /v1/ekb/kbs/{kb}/ikus → { kb, ikus: [{id, titulo, resumen, tipo, tipo_etiqueta, estado, etiqueta, version}], tipos }
//   POST /v1/ekb/ikus {kb_id, titulo, resumen, tipo, texto, vigencia?, confidencial?} → 201 { iku }   (confidencial: 409 etiqueta_no_confidencial si el KB no lo es)
//   GET  /v1/ekb/ikus/{id} → { iku: cabecera + texto + contenedores + versiones }
//   GET  /v1/ekb/ikus/{id}/versiones/{n} → { iku, version_vista }
//   PUT  /v1/ekb/ikus/{id} {titulo?, resumen?, tipo?, texto?, vigencia?} → { iku }
// ---------------------------------------------------------------------------
const MOCK_TIPOS = [
  ['hecho', 'Hecho'], ['procedimiento', 'Procedimiento'], ['decision', 'Decisión'], ['leccion', 'Lección'],
  ['mapa_de_datos', 'Mapa de datos'], ['indicador', 'Indicador'], ['referencia', 'Referencia'],
].map(([id, etiqueta]) => ({ id, etiqueta, disponible: !['mapa_de_datos', 'indicador'].includes(id) }))
const MOCK_IKUS = {}

export async function listIkus(kbId, estado = 'vigente') {
  if (USE_MOCK) {
    const kb = MOCK_EKB.kbs.find(k => k.id === kbId)
    return { kb: { id: kbId, nombre: kb?.nombre, estado: kb?.estado }, ikus: Object.values(MOCK_IKUS).filter(i => i.kb === kbId && (estado === 'todos' || i.actual.estado === estado)).map(i => ({ ...i.actual, resumen_mostrado: i.actual.resumen || (i.actual.texto.split(/(?<=[.!?])\s/)[0] ?? '') })), tipos: MOCK_TIPOS }
  }
  return ekbFetch(`/kbs/${encodeURIComponent(kbId)}/ikus?estado=${encodeURIComponent(estado)}`, 'listIkus')
}

export async function crearIku({ kb_id, titulo, resumen, tipo, texto, vigencia, confidencial }) {
  if (USE_MOCK) {
    const id = `iku-${Object.keys(MOCK_IKUS).length + 1}`
    const iku = { id, titulo, resumen, tipo, tipo_etiqueta: tipo, nivel: 'conocimiento', vigencia: vigencia || null, origen: 'alta manual', version: 1, tokens: Math.ceil(texto.length / 4), texto, estado: 'vigente', etiqueta: 'interna', contenedores: [{ id: kb_id, nombre: MOCK_EKB.kbs.find(k => k.id === kb_id)?.nombre }], versiones: [{ version: 1, autor: 'admin@mock.test', tokens: 1, creado_en: new Date().toISOString() }] }
    MOCK_IKUS[id] = { kb: kb_id, actual: iku, historial: { 1: iku } }
    mockEkb('iku_creado', kb_id, { iku_id: id, titulo, kb: iku.contenedores[0].nombre })
    return { iku }
  }
  return ekbFetch('/ikus', 'crearIku', { method: 'POST', body: JSON.stringify({ kb_id: kb_id || null, titulo, resumen: resumen || '', tipo, texto, vigencia: vigencia || null, ...(confidencial ? { confidencial: true } : {}) }) })
}

export async function getIku(id) {
  if (USE_MOCK) return { iku: MOCK_IKUS[id].actual }
  return ekbFetch(`/ikus/${encodeURIComponent(id)}`, 'getIku')
}

export async function getVersionIku(id, version) {
  if (USE_MOCK) return { iku: MOCK_IKUS[id].historial[version], version_vista: version }
  return ekbFetch(`/ikus/${encodeURIComponent(id)}/versiones/${version}`, 'getVersionIku')
}

export async function editarIku(id, cambios) {
  if (USE_MOCK) {
    const m = MOCK_IKUS[id]
    const iku = { ...m.actual, ...cambios, version: m.actual.version + 1 }
    iku.versiones = [...m.actual.versiones, { version: iku.version, autor: 'admin@mock.test', tokens: 1, creado_en: new Date().toISOString() }]
    m.actual = iku
    m.historial[iku.version] = iku
    mockEkb('iku_editado', m.kb, { iku_id: id, titulo: iku.titulo, version: iku.version })
    return { iku }
  }
  return ekbFetch(`/ikus/${encodeURIComponent(id)}`, 'editarIku', { method: 'PUT', body: JSON.stringify(cambios) })
}

// ---------------------------------------------------------------------------
// Un iKu en varios KBs — GEN-EKB-011
//   GET  /v1/ekb/kbs/buscar?q=&iku_id= → { kbs: [{id, nombre, etiqueta}] } (activos que puedo usar; sin texto, vacío)
//   POST /v1/ekb/ikus/{id}/kbs {kb_id} → { iku }  (el mismo iKu; etiqueta efectiva = la más restrictiva)
//   POST /v1/ekb/ikus/{id}/kbs/{kb}/quitar → { resultado: 'quitado'|'pendiente', iku, quita }
//        409 ultimo_contenedor: no se quita del último KB, se retira el iKu
//   POST /v1/ekb/quitas/{id}/confirmar | /rechazar → { quita, kb }   (solo el titular del KB)
//   GET  /v1/ekb/pendientes → { pendientes, quitas: [{id, tipo:'iku'|'kb', kb, iku?, de, a, ikus_afectados, propuesto_por}] }
// ---------------------------------------------------------------------------
export async function buscarKbs(q, ikuId = null) {
  if (USE_MOCK) {
    const t = (q || '').trim().toLowerCase()
    const ya = (MOCK_IKUS[ikuId]?.actual.contenedores ?? []).map(c => c.id)
    return { kbs: t ? MOCK_EKB.kbs.filter(k => k.estado === 'activo' && !ya.includes(k.id) && k.nombre.toLowerCase().includes(t)).map(k => ({ id: k.id, nombre: k.nombre, etiqueta: k.etiqueta })) : [] }
  }
  const params = new URLSearchParams({ q: q || '' })
  if (ikuId) params.set('iku_id', ikuId)
  return ekbFetch(`/kbs/buscar?${params}`, 'buscarKbs')
}

export async function anadirIkuAKb(ikuId, kbId) {
  if (USE_MOCK) {
    const m = MOCK_IKUS[ikuId]
    const kb = MOCK_EKB.kbs.find(k => k.id === kbId)
    const rango = { publica: 1, interna: 2, confidencial: 3 }
    if (rango[kb.etiqueta] > rango[m.actual.etiqueta]) m.actual.etiqueta = kb.etiqueta
    m.actual.contenedores = [...m.actual.contenedores, { id: kb.id, nombre: kb.nombre, estado: 'activo' }]
    mockEkb('iku_kb_anadido', kbId, { iku_id: ikuId, kb: kb.nombre })
    return { iku: m.actual }
  }
  return ekbFetch(`/ikus/${encodeURIComponent(ikuId)}/kbs`, 'anadirIkuAKb', { method: 'POST', body: JSON.stringify({ kb_id: kbId }) })
}

export async function quitarIkuDeKb(ikuId, kbId) {
  if (USE_MOCK) {
    const m = MOCK_IKUS[ikuId]
    if (m.actual.contenedores.length < 2) throw Object.assign(new Error('ultimo_contenedor'), { code: 'ultimo_contenedor' })
    m.actual.contenedores = m.actual.contenedores.filter(c => c.id !== kbId)
    mockEkb('iku_kb_quitado', kbId, { iku_id: ikuId })
    return { resultado: 'quitado', iku: m.actual, quita: null }
  }
  return ekbFetch(`/ikus/${encodeURIComponent(ikuId)}/kbs/${encodeURIComponent(kbId)}/quitar`, 'quitarIkuDeKb', { method: 'POST' })
}

// GEN-EKB-043 · un iKu enlazado en otro KB (sin copia): quitar el enlace no retira el iKu
//   POST /v1/ekb/ikus/{id}/enlaces/{kb}/quitar → { iku }
export async function desenlazarIku(ikuId, kbId) {
  if (USE_MOCK) return { iku: MOCK_IKUS[ikuId]?.actual ?? null }
  return ekbFetch(`/ikus/${encodeURIComponent(ikuId)}/enlaces/${encodeURIComponent(kbId)}/quitar`, 'desenlazarIku', { method: 'POST' })
}

// GEN-EKB-012 · retirar un iKu (no se borra: no hay DELETE)
//   GET  /v1/ekb/kbs/{kb}/ikus?estado=vigente|retirado|todos → filas con estado y retirado_en
//   POST /v1/ekb/ikus/{id}/retirar → { iku }   409 iku_retirado si ya lo estaba
export async function retirarIku(ikuId) {
  if (USE_MOCK) {
    const m = MOCK_IKUS[ikuId]
    m.actual = { ...m.actual, estado: 'retirado', retirado_en: new Date().toISOString() }
    mockEkb('iku_retirado', m.kb, { iku_id: ikuId })
    return { iku: m.actual }
  }
  return ekbFetch(`/ikus/${encodeURIComponent(ikuId)}/retirar`, 'retirarIku', { method: 'POST' })
}

export async function resolverQuita(id, confirmar) {
  if (USE_MOCK) return { quita: { id } }
  return ekbFetch(`/quitas/${encodeURIComponent(id)}/${confirmar ? 'confirmar' : 'rechazar'}`, 'resolverQuita', { method: 'POST' })
}

// GEN-EKB-014 · índice de búsqueda (solo admin). Buscar es de E03: aquí solo salud y reconstrucción.
//   GET  /v1/ekb/indice/salud → { indice: { al_dia, vigentes, proyectados, sin_proyectar, desfasados,
//        otro_modelo, sobrantes, modelos, modelo_vigente, embeddings_disponible, ultima_reconstruccion } }
//   POST /v1/ekb/indice/reconstruir → { reconstruccion: { vigentes, proyectados, fallidos, quitados, modelo }, indice }
function mockSaludIndice() {
  const vigentes = Object.values(MOCK_IKUS).filter(m => m.actual.estado === 'vigente').length
  const ultima = MOCK_EKB.traza.find(t => t.accion === 'indice_reconstruido')
  return {
    al_dia: true, vigentes, proyectados: vigentes, sin_proyectar: 0, desfasados: 0, otro_modelo: 0, sobrantes: 0,
    modelos: vigentes ? [{ modelo: 'cohere.embed-v4', modelo_version: '0', dimension: 1024, ikus: vigentes }] : [],
    modelo_vigente: { modelo: 'cohere.embed-v4', modelo_version: '0', dimension: 1024 }, embeddings_disponible: true,
    ultima_reconstruccion: ultima ? { ts: ultima.ts, actor: ultima.actor, detalle: ultima.detalle } : null,
    huerfanos: { disponible: true, md: 0, ikus: 0, recientes: 0, no_reconocidos: 0 },
  }
}

export async function getSaludIndice() {
  if (USE_MOCK) return { indice: mockSaludIndice() }
  return ekbFetch('/indice/salud', 'getSaludIndice')
}

export async function reconstruirIndice() {
  if (USE_MOCK) {
    const s = mockSaludIndice()
    const reconstruccion = { vigentes: s.vigentes, proyectados: s.vigentes, fallidos: 0, quitados: 0, modelo: 'cohere.embed-v4:0' }
    mockEkb('indice_reconstruido', null, reconstruccion)
    return { reconstruccion, indice: mockSaludIndice() }
  }
  return ekbFetch('/indice/reconstruir', 'reconstruirIndice', { method: 'POST' })
}

// GEN-EKB-016 · .md huérfanos del almacén de iKus (sin fila en la base). La salud trae
//   indice.huerfanos = { disponible, md, ikus, recientes, no_reconocidos } o { disponible: false }.
//   POST /v1/ekb/indice/huerfanos/barrer → { barrido: { quitados, ikus, fallidos, recientes_conservados,
//        no_reconocidos, rutas? }, indice }
export async function barrerHuerfanos() {
  if (USE_MOCK) {
    const barrido = { quitados: 0, ikus: 0, fallidos: 0, recientes_conservados: 0, no_reconocidos: 0 }
    mockEkb('md_huerfanos_quitados', null, barrido)
    return { barrido, indice: mockSaludIndice() }
  }
  return ekbFetch('/indice/huerfanos/barrer', 'barrerHuerfanos', { method: 'POST' })
}

// ===========================================================================
// Administración de usuarios y roles — US-26
// Contrato admin PROPUESTO en US-26 (endpoints /v1/admin/users): pendiente de
// VoBo de Cassie (es su contrato) y de los endpoints reales del kernel. NO es
// un acuerdo cerrado todavía. Hoy el kernel real no expone estas rutas ni el
// rol/scope admin; esta capa vive contra mock hasta la convergencia.
// Aislamiento por tenant y validación de permiso admin -> en el KERNEL; el
// front solo pinta y mapea errores (R-PT-01/R-PT-04). En cada endpoint el
// backend debe responder 403 si el usuario no es admin.
// ---------------------------------------------------------------------------
// GET /v1/admin/users → { users: [{ id, display_name, email, rol, estado }] }
// ---------------------------------------------------------------------------
export async function listUsers() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 300))
    return { users: listMockUsers() }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/admin/users`, { headers: authHeaders(false) }),
    'listUsers',
  )
  if (!res.ok) throw await apiError(res, 'listUsers')
  return res.json()
}

// ---------------------------------------------------------------------------
// GET /v1/entrada/empleados → { empleados: [{ nombre, cargo, rol, estado }] } — GEN-PX-012.
// Lo puede leer CUALQUIER usuario del tenant (el de /admin/users es solo admin): solo activos, sin
// email ni id. `cargo` es null mientras el directorio no lo guarde.
// ---------------------------------------------------------------------------
export async function listEmpleados() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 200))
    return { empleados: empleadosMock(empleadosPedidos(typeof window !== 'undefined' ? window.location?.search ?? '' : '')) }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/entrada/empleados`, { headers: authHeaders(false) }),
    'listEmpleados',
  )
  if (!res.ok) throw await apiError(res, 'listEmpleados')
  return res.json()
}

// ---------------------------------------------------------------------------
// POST /v1/admin/users  body { email, rol, nombre } → 201 { user } (estado "pendiente")
// Invitar = AdminCreateUser (Cognito) + email de invitación (SES) en el kernel.
// Errores: 403 forbidden · 409 email_already_exists · 422 invalid_*
// ---------------------------------------------------------------------------
export async function inviteUser({ email, rol, nombre }) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 400))
    return { user: inviteMockUser({ email, rol, nombre }) }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/admin/users`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ email, rol, nombre }),
    }),
    'inviteUser',
  )
  if (!res.ok) throw await apiError(res, 'inviteUser')
  return res.json()
}

// ---------------------------------------------------------------------------
// PATCH /v1/admin/users/{user_id}  body { rol } → 200 { user }
// Cambiar rol = grupos Cognito en el kernel. Errores: 403 · 404 · 422.
// ---------------------------------------------------------------------------
export async function changeUserRole(userId, rol) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 300))
    return { user: changeMockUserRole(userId, rol) }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/admin/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ rol }),
    }),
    'changeUserRole',
  )
  if (!res.ok) throw await apiError(res, 'changeUserRole')
  return res.json()
}

// ---------------------------------------------------------------------------
// Mock de historiales de chat
// ---------------------------------------------------------------------------
const MOCK_CHAT_HISTORY = {
  'chat-001': [
    {
      id: 'h-001-1',
      role: 'user',
      content: '¿En qué estado está el contrato del descriptor de sesión?',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 - 5000).toISOString(),
    },
    {
      id: 'h-001-2',
      role: 'agent',
      agent: { nombre: 'Cassie' },
      type: 'answer',
      payload: {
        markdown: 'El contrato del descriptor de sesión está en **v1.0**, congelado desde el 2026-06-25. Incluye el esquema de bootstrap (§3), la política de sesión (§4) y el contrato de eventos SSE (§5). Listo para construir contra mock.',
      },
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
  ],
  'chat-002': [
    {
      id: 'h-002-1',
      role: 'user',
      content: '¿Cuál es el plan para el onboarding de Koana?',
      timestamp: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'h-002-2',
      role: 'agent',
      agent: { nombre: 'Cassie' },
      type: 'answer',
      payload: {
        markdown: 'El onboarding de Koana sigue el track de proyecto ORIGEN. El primer paso es preparar el proyecto ORIGEN para recibirlos — milestone F040. Las primeras acciones de colaboración en KOANA arrancan en F050.',
      },
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    },
  ],
  'chat-003': [],
}

// POST /v1/entrada/resumen-dia — GEN-PX-009: titular y entradilla del día que escribe el agente
// enrutador (Anne). El kernel regenera solo si hay novedades desde el último resumen; si no, devuelve
// el guardado sin llamar al modelo. La entradilla llega troceada: los segmentos con `chat_id` son los
// nombres en negrita que abren esa conversación (ya validados por el kernel). Falla con 503
// `resumen_no_disponible`: el llamador deja el saludo sin texto.
// -> { estado: 'ok' | 'sin_novedades', titular, entradilla: [{texto, chat_id?}],
//      agente: {id, nombre, rol}, generado_en, regenerado }
export async function getResumenDia() {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 900))
    return {
      estado: 'ok',
      titular: 'Ayer tuviste dos conversaciones con el equipo y una sigue abierta',
      entradilla: [
        { texto: 'Hablaste con ' },
        { texto: 'Cassie', chat_id: 'chat-001' },
        { texto: ' del contrato del descriptor y con ' },
        { texto: 'Seo', chat_id: 'chat-002' },
        { texto: ' del onboarding de Koana. La de ' },
        { texto: 'Limpieza de Odoo', chat_id: 'chat-003' },
        { texto: ' lleva tres días sin actividad.' },
      ],
      agente: { id: 'anne', nombre: 'Anne', rol: 'Chief of Staff' },
      generado_en: new Date().toISOString(),
      regenerado: true,
    }
  }
  const res = checkAuth(
    await fetch(`${apiBase()}/entrada/resumen-dia`, { method: 'POST', headers: authHeaders(false) }),
    'getResumenDia',
  )
  if (!res.ok) throw await apiError(res, 'getResumenDia')
  return res.json()
}
